/** The M0 look-dev diorama: every biome, time of day and weather under the real pipeline (05 §12). */

import { Color, DirectionalLight, HemisphereLight, Mesh, PointLight, Scene, Vector3 } from 'three';
import { PixelCamera } from '../render/camera/PixelCamera';
import { Soldier, type SoldierLook } from '../render/characters/soldier';
import { broadleaf, cactus, deadTree, grassTuft, type KitBuilder, palm, pine, rock, shrub } from '../render/kit/flora';
import { MeshBuilder, place } from '../render/kit/MeshBuilder';
import { barrel, bunker, canopy, crate, fuelTank, hut, mgNest, radar, sandbagRun, supplyPod, tent, wallRun, watchtower, wireRun } from '../render/kit/structures';
import { createEmissiveMaterial, createGradientMap, createToonMaterial, OUTLINE } from '../render/materials/toon';
import { type BiomeName, getGrade, type Grade, Palette, type TimeOfDay } from '../render/palette';
import { buildTerrain } from '../render/terrain/Terrain';
import { Weather, type WeatherKind } from '../render/vfx/weather';
import { Pcg32 } from '../sim/rng';
import { makeTerrainDef, roadDistance, trenchZ } from './lookdevTerrain';

export interface LookdevParams {
  biome: BiomeName;
  tod: TimeOfDay;
  weather: WeatherKind;
  seed: number;
  pan: boolean;
  /** Look-dev knobs for settling camera framing (05 §3). */
  pitch: number;
  ppm: number;
}

export interface LookdevScene {
  scene: Scene;
  camera: PixelCamera;
  palette: Palette;
  grade: Grade;
  update(time: number, dt: number): void;
  /** Move the camera focus (look-dev checks); ignored while `pan` is on. */
  setFocus(x: number, z: number): void;
}

/* The diorama is 64 x 48 m; the camera shows 40 x 27.5 m of it. North is -z (up-screen). */

export function createLookdev(params: LookdevParams): LookdevScene {
  const palette = new Palette(params.biome);
  const grade = getGrade(params.tod);
  const rng = new Pcg32(params.seed, 1);
  const scene = new Scene();
  const gradient = createGradientMap();
  const camera = new PixelCamera(params.ppm, params.pitch);

  // --- lights (05 §7)
  const sunDir = new Vector3(...grade.sunDir).normalize();
  const sun = new DirectionalLight(new Color(grade.sunColor), grade.sunIntensity);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  const sc = sun.shadow.camera;
  sc.left = -32;
  sc.right = 32;
  sc.top = 32;
  sc.bottom = -32;
  sc.near = 10;
  sc.far = 220;
  sun.shadow.bias = -0.0006;
  sun.shadow.normalBias = 0.04;
  scene.add(sun, sun.target);
  scene.add(new HemisphereLight(new Color(grade.skyColor), new Color(grade.groundColor), grade.hemiIntensity));

  // --- ground and water
  const def = makeTerrainDef(params.biome, params.seed);
  const terrain = buildTerrain(def, palette, gradient);
  scene.add(terrain.ground);
  if (terrain.water) scene.add(terrain.water);
  const groundY = (x: number, z: number): number => def.heightAt(x, z);

  // --- static props: one merged mesh per outline class keeps draw calls trivial
  const props = new MeshBuilder();
  const clutter = new MeshBuilder();
  const interactive = new MeshBuilder();
  const put = (target: MeshBuilder, builder: KitBuilder, x: number, z: number, yaw = 0, scale = 1): void => {
    target.merge(builder(palette, rng.fork(`${x},${z}`)), place(x, groundY(x, z) - 0.05, z, yaw, scale));
  };

  const tree: KitBuilder = params.biome === 'arctic' ? pine : params.biome === 'desert' ? palm : broadleaf;
  const scrub: KitBuilder = params.biome === 'desert' ? cactus : shrub;
  const treeSpots: Array<[number, number]> = [
    [-19, -13], [-15.5, -15], [-11, -14.5], [-7.5, -12.5], [-4, -15], [-17.5, -9.5], [-1, -12],
    [-21, 9.5], [-18, 12.5], [-6, 11.5], [16.5, 10.5], [20, 7.5], [22.5, -2], [-23, -2], [1.5, -16],
  ];
  for (const [x, z] of treeSpots) put(props, tree, x + rng.range(-0.6, 0.6), z + rng.range(-0.6, 0.6), rng.range(0, 6.28), rng.range(0.9, 1.15));
  put(props, deadTree, -2.5, -9.5, 1.0);
  put(props, deadTree, 5.5, 12.5, 2.2);
  for (const [x, z] of [[-3, -2.5], [1.8, 7.4], [-15.5, 2.2], [4.2, 10.8], [-8, 1.2], [21, 12], [-22, 5]] as const) {
    props.merge(rock(palette, rng.fork(`rock${x}`), {}), place(x, groundY(x, z) - 0.1, z));
  }
  for (const [x, z] of [[-6.5, 3.4], [-13, 3.2], [-10, 9.6], [0.5, 2.5], [7, 8], [14.5, 6.5], [-19.5, 4.4], [2.5, -4], [-5, 8.8], [18, 13]] as const) {
    put(props, scrub, x, z, rng.range(0, 6.28));
  }

  // The compound in the north-east.
  put(props, bunker, 6.5, -7.5);
  put(props, hut, 14.5, -8.5);
  put(props, params.biome === 'arctic' ? radar : tent, 16.5, -2.2);
  put(props, watchtower, 19, 1.2);
  put(props, mgNest, 8.2, 1.2);
  put(props, fuelTank, 9.5, -12.2);
  props.merge(wallRun(palette, rng, 16), place(11.5, groundY(11.5, -14.2), -14.2));
  props.merge(wireRun(palette, rng, 8), place(5, groundY(5, 3.4), 3.4));
  props.merge(wireRun(palette, rng, 6), place(16.5, groundY(16.5, 3.4), 3.4));
  props.merge(sandbagRun(palette, rng, 3.6), place(12.8, groundY(12.8, 2.4), 2.4));
  for (const [x, z] of [[11.2, -5.2], [12.1, -5.6], [11.5, -4.3]] as const) put(props, crate, x, z, rng.range(0, 1));
  for (const [x, z] of [[17.2, -6.2], [17.9, -6.5], [17.5, -5.6], [4.4, -3.6]] as const) put(props, barrel, x, z);

  // The ranger's side: a pod and the canopy that brought it.
  put(interactive, supplyPod, -4.2, 9.2, 0.4);
  put(props, canopy, -2.0, 10.4);

  const tuft: KitBuilder = grassTuft;
  if (params.biome !== 'arctic') {
    for (let i = 0; i < 260; i++) {
      const x = rng.range(-30, 30);
      const z = rng.range(-22, 22);
      if (roadDistance(x, z) < 2.4 || groundY(x, z) < -0.2 || (x > 2 && x < 21 && z > -13 && z < 3)) continue;
      put(clutter, tuft, x, z, rng.range(0, 6.28), params.biome === 'desert' ? 0.8 : 1);
    }
  }

  const propMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.prop });
  const addStatic = (mb: MeshBuilder, material = propMaterial): void => {
    if (mb.triangleCount === 0) return;
    const mesh = new Mesh(mb.build(), material);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    scene.add(mesh);
  };
  addStatic(props);
  addStatic(clutter, createToonMaterial({ gradientMap: gradient, outline: OUTLINE.none }));
  addStatic(interactive, createToonMaterial({ gradientMap: gradient, outline: OUTLINE.interactive }));

  // Lit window on the hut + lamp, so night has warm anchors.
  const glow = new MeshBuilder();
  glow.box(0.8, 0.7, 0.06, palette.color('fire.2'), place(15.5, groundY(14.5, -8.5) + 1.0, -6.72));
  const glowMesh = new Mesh(glow.build(), createEmissiveMaterial(gradient, params.tod === 'night' ? 2.4 : 0.6));
  scene.add(glowMesh);
  if (params.tod === 'night' || params.tod === 'dusk') {
    const lamp = new PointLight(new Color(palette.hex('fire.2')), 38, 11, 1.6);
    lamp.position.set(15.5, 1.6, -5.6);
    scene.add(lamp);
    const yard = new PointLight(new Color(palette.hex('fire.3')), 60, 14, 1.5);
    yard.position.set(10, 3.2, -3);
    scene.add(yard);
  }

  // --- characters
  const charMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.character });
  const playerMaterial = createToonMaterial({ gradientMap: gradient, outline: OUTLINE.player });
  const soldiers: Soldier[] = [];
  const spawn = (look: SoldierLook, x: number, z: number, facing: number, motion: Soldier['state']['motion']): Soldier => {
    const s = new Soldier(palette, look.side === 'ranger' ? playerMaterial : charMaterial, look);
    s.state.position.set(x, groundY(x, z), z);
    s.state.facing = facing;
    s.state.motion = motion;
    scene.add(s.mesh);
    soldiers.push(s);
    return s;
  };
  const RANGER: SoldierLook = { side: 'ranger', skin: 'skinB', headgear: 'helmet', pack: true };
  const RIFLEMAN: SoldierLook = { side: 'enemy', skin: 'skinA', headgear: 'helmet', pack: false };
  const ELITE: SoldierLook = { side: 'enemy', skin: 'skinC', headgear: 'beret', pack: false };
  const OFFICER: SoldierLook = { side: 'officer', skin: 'skinA', headgear: 'cap', pack: false };

  spawn(RANGER, -0.8, 5.2, Math.PI * 0.85, 'aim');
  const crawler = spawn(RANGER, -12, trenchZ(-12), Math.PI / 2, 'crawl');
  const runner = spawn(RANGER, -8, 12, Math.PI / 2, 'run');
  spawn(RIFLEMAN, 10.2, 3.6, 0.3, 'idle');
  spawn(OFFICER, 12.6, -3.4, -0.6, 'idle');
  spawn(ELITE, 3.4, -3.2, 0.9, 'idle');
  spawn(RIFLEMAN, 3.2, 1.0, 2.2, 'dead');
  const patrolA = spawn(RIFLEMAN, 18, 12, 0, 'walk');
  const patrolB = spawn(RIFLEMAN, 19.2, 13.3, 0, 'walk');

  const weather = new Weather(params.weather, palette.color(params.biome === 'desert' ? 'ground.3' : 'paper.0'));
  if (weather.points) scene.add(weather.points);

  const focus = new Vector3(0, 0, 0);
  // Light-space basis, matching how three.js orients a directional light's shadow camera.
  const lightRight = new Vector3(0, 1, 0).cross(sunDir).normalize();
  const lightUp = sunDir.clone().cross(lightRight).normalize();
  const shadowTarget = new Vector3();
  const patrolFrom = new Vector3(21, 0, 15);
  const patrolTo = new Vector3(11.5, 0, 5.5);

  /** Ping-pong a soldier between two points at `speed`, keeping gait and facing in step. */
  const shuttle = (s: Soldier, a: Vector3, b: Vector3, speed: number, time: number, lag: number): void => {
    const length = a.distanceTo(b);
    const d = Math.max(0, time - lag) * speed;
    const lap = Math.floor(d / length);
    const along = d - lap * length;
    const forward = lap % 2 === 0;
    const t = (forward ? along : length - along) / length;
    const x = a.x + (b.x - a.x) * t;
    const z = a.z + (b.z - a.z) * t;
    s.state.position.set(x, groundY(x, z), z);
    const dx = (b.x - a.x) * (forward ? 1 : -1);
    const dz = (b.z - a.z) * (forward ? 1 : -1);
    s.state.facing = Math.atan2(dx, dz);
    s.state.travelled = d;
  };

  const trenchA = new Vector3(-19, 0, 0);
  const trenchB = new Vector3(-4, 0, 0);

  return {
    scene,
    camera,
    palette,
    grade,
    setFocus(x, z) {
      focus.set(x, 0, z);
    },
    update(time) {
      if (params.pan) {
        focus.set(Math.sin(time * 0.23) * 9, 0, Math.cos(time * 0.17) * 6);
      }
      camera.focus.copy(focus);
      camera.update();

      // Keep the shadow frustum on the view. The shadow map's texel grid lies along the
      // LIGHT's right/up axes, so that is where the target must be snapped - snapping in
      // world x/z still lets shadow edges crawl as the camera scrolls.
      const texel = 64 / 2048;
      const px = camera.focus.dot(lightRight);
      const py = camera.focus.dot(lightUp);
      const ox = Math.round(px / texel) * texel - px;
      const oy = Math.round(py / texel) * texel - py;
      shadowTarget.copy(camera.focus).addScaledVector(lightRight, ox).addScaledVector(lightUp, oy);
      sun.target.position.copy(shadowTarget);
      sun.position.copy(shadowTarget).addScaledVector(sunDir, 120);
      sun.target.updateMatrixWorld();

      shuttle(patrolA, patrolFrom, patrolTo, 1.5, time, 0);
      shuttle(patrolB, patrolFrom, patrolTo, 1.5, time, 1.1);
      shuttle(runner, new Vector3(-20, 0, 13.5), new Vector3(2, 0, 12.5), 4.6, time, 0);
      // The crawler follows the trench floor.
      shuttle(crawler, trenchA, trenchB, 0.9, time, 0);
      const cx = crawler.state.position.x;
      crawler.state.position.set(cx, groundY(cx, trenchZ(cx)), trenchZ(cx));

      for (const s of soldiers) s.update(time, camera);
      weather.update(time, camera.focus);
    },
  };
}
